/**
 * EmptyEnvironmentModal - Confirmation modal for the Empty Environment action
 *
 * Fetches guard conditions from the API, displays impact summary,
 * and requires typing "DELETE ALL" to confirm.
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchEnvironmentStatus } from '../../api';

// ---------- Styles ----------

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalStyle = {
  background: '#1a1a1a',
  border: '1px solid #333',
  borderRadius: 12,
  padding: '24px 32px',
  maxWidth: 520,
  width: '90%',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 20,
};

const titleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: '#fff',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#888',
  fontSize: 20,
  cursor: 'pointer',
  padding: '4px 8px',
  lineHeight: 1,
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#888',
  marginBottom: 8,
};

const panelStyle = {
  background: '#111',
  border: '1px solid #333',
  borderRadius: 8,
  padding: '12px 16px',
  marginBottom: 20,
};

const guardRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
  fontSize: 14,
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: '#111',
  border: '1px solid #444',
  borderRadius: 6,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const footerStyle = {
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-end',
  marginTop: 20,
};

const cancelButtonStyle = {
  padding: '10px 24px',
  background: '#333',
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};

// ---------- Helpers ----------

function formatDuration(minutes) {
  if (minutes == null) return 'unknown';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m ago`;
}

function formatSeconds(s) {
  if (s == null || s <= 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function RateLimitCountdown({ initialSeconds, forceOverride, onToggleOverride }) {
  const [remaining, setRemaining] = useState(Math.max(0, initialSeconds || 0));

  useEffect(() => {
    if (remaining <= 0) return;
    const interval = setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [remaining > 0]);

  return (
    <div style={{ marginTop: 4, paddingLeft: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ color: '#888', fontSize: 12, fontFamily: '"IBM Plex Mono", monospace' }}>
        {formatSeconds(remaining)} remaining
      </span>
      <button
        onClick={onToggleOverride}
        style={{
          background: forceOverride ? '#dc3545' : 'transparent',
          border: `1px solid ${forceOverride ? '#dc3545' : '#666'}`,
          borderRadius: 4,
          color: forceOverride ? '#fff' : '#888',
          fontSize: 11,
          padding: '2px 8px',
          cursor: 'pointer',
        }}
      >
        {forceOverride ? 'Override active' : 'Override'}
      </button>
    </div>
  );
}

function GuardRow({ label, passed }) {
  return (
    <div style={guardRowStyle}>
      <span style={{ color: '#ccc' }}>{label}</span>
      <span style={{
        color: passed ? '#66ffb7' : '#ff6b6b',
        fontWeight: 600,
        fontSize: 13,
      }}>
        {passed ? 'passed' : 'failed'}
      </span>
    </div>
  );
}

// ---------- Main component ----------

function EmptyEnvironmentModal({ isOpen, onClose, onConfirm, stats }) {
  const [guardStatus, setGuardStatus] = useState(null);
  const [guardLoading, setGuardLoading] = useState(true);
  const [guardError, setGuardError] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Fetch guard conditions on mount (component is conditionally rendered)
  useEffect(() => {
    let cancelled = false;
    fetchEnvironmentStatus()
      .then((data) => {
        if (!cancelled) setGuardStatus(data);
      })
      .catch((err) => {
        console.error('Failed to fetch environment status:', err);
        if (!cancelled) setGuardError('Failed to check guard conditions');
      })
      .finally(() => {
        if (!cancelled) setGuardLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const [forceOverride, setForceOverride] = useState(false);

  // Core guards (no live matches + cooldown expired) must always pass
  const coreGuardsPassed = guardStatus?.no_live_matches && guardStatus?.cooldown_expired;
  // Rate limit can be overridden when core guards pass
  const rateLimitPassed = guardStatus?.rate_limit_expired !== false;
  const allGuardsPassed = coreGuardsPassed && (rateLimitPassed || forceOverride);
  const confirmValid = confirmText === 'DELETE ALL';
  const canSubmit = allGuardsPassed && confirmValid && !submitting;

  const handleConfirm = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({ force: forceOverride });
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to empty environment');
      setSubmitting(false);
    }
  }, [canSubmit, onConfirm, forceOverride]);

  if (!isOpen) return null;

  // Build impact summary from stats
  const total = stats?.total ?? 0;
  const byStatus = stats?.by_status || {};
  const done = byStatus.done || 0;
  const exported = byStatus.exported || 0;
  const pending = total - done - exported;

  const confirmButtonStyle = {
    padding: '10px 24px',
    background: canSubmit ? '#dc3545' : '#555',
    border: 'none',
    borderRadius: 6,
    color: canSubmit ? '#fff' : '#888',
    cursor: canSubmit ? 'pointer' : 'not-allowed',
    fontSize: 14,
    fontWeight: 600,
    opacity: submitting ? 0.7 : 1,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={titleStyle}>Empty Environment</h3>
          <button style={closeButtonStyle} onClick={onClose} title="Close">
            X
          </button>
        </div>

        {/* Guard Status */}
        <div style={sectionLabelStyle}>Guard Status</div>
        <div style={panelStyle}>
          {guardLoading && (
            <div style={{ color: '#888', fontSize: 14 }}>Checking conditions...</div>
          )}
          {guardError && (
            <div style={{ color: '#ff6b6b', fontSize: 14 }}>{guardError}</div>
          )}
          {guardStatus && !guardLoading && (
            <>
              <GuardRow
                label="No live matches"
                passed={guardStatus.no_live_matches}
              />
              <GuardRow
                label="Cooldown expired"
                passed={guardStatus.cooldown_expired}
              />
              {guardStatus.last_match_ended_minutes != null && (
                <div style={{ color: '#888', fontSize: 12, marginTop: 4, paddingLeft: 8 }}>
                  Last match ended: {formatDuration(guardStatus.last_match_ended_minutes)}
                </div>
              )}
              <GuardRow
                label="Rate limit"
                passed={guardStatus.rate_limit_expired !== false}
              />
              {guardStatus.rate_limit_expired === false && coreGuardsPassed && (
                <RateLimitCountdown
                  initialSeconds={guardStatus.rate_limit_remaining_seconds}
                  forceOverride={forceOverride}
                  onToggleOverride={() => setForceOverride(!forceOverride)}
                />
              )}
            </>
          )}
        </div>

        {/* Impact Summary */}
        <div style={sectionLabelStyle}>Impact Summary</div>
        <div style={panelStyle}>
          <div style={{ color: '#ff6b6b', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {total.toLocaleString()} photo{total !== 1 ? 's' : ''} will be permanently deleted
          </div>
          <div style={{ color: '#888', fontSize: 13 }}>
            {done.toLocaleString()} done / {exported.toLocaleString()} exported / {pending.toLocaleString()} pending
          </div>
        </div>

        {/* Type-to-confirm */}
        <div style={{ marginBottom: 4, color: '#ccc', fontSize: 14 }}>
          To confirm, type <strong style={{ color: '#fff' }}>DELETE ALL</strong> below:
        </div>
        <input
          style={inputStyle}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE ALL"
          autoFocus
          disabled={submitting}
        />

        {/* Error message */}
        {submitError && (
          <div style={{ color: '#ff6b6b', fontSize: 13, marginTop: 8 }}>
            {submitError}
          </div>
        )}

        {/* Footer */}
        <div style={footerStyle}>
          <button style={cancelButtonStyle} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            style={confirmButtonStyle}
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? 'Emptying...' : 'Empty Environment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmptyEnvironmentModal;
